import { useQuery } from '@tanstack/react-query'
import { OrderHistoryReceiptMeal } from '@tovala/browser-apis-combinedapi'
import { ButtonLoading } from '@tovala/component-library'
import { events } from 'analytics/events'
import { clsx } from 'clsx'
import APIErrorDisplay from 'components/common/APIErrorDisplay'
import { useCreateMealReview } from 'components/core/hooks/reviews'
import { reviewQueries } from 'components/core/queries/reviews'
import { useUser } from 'contexts/user'
import { AnimatePresence } from 'motion/react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useLocation } from 'react-router-dom'
import { track } from 'utils/analytics'
import { getMealImageURL } from 'utils/meals'

import MealRatingBadge from 'components/core/menu/MealRatingBadge'

import RatingOnboardingCallout from './RatingOnboardingCallout'
import {
  MealReviewFormValues,
  RatingChipOptions,
  RatingStars,
  ReviewCommentField,
  SharePubliclyToggle,
} from './ReviewFields'
import {
  CREATE_REVIEW_ERRORS,
  getCreateMealReviewData,
  getLastSharePubliclyChoice,
  getRatingChipsForRating,
  getReviewSourceID,
  LOAD_RATING_CHIPS_ERRORS,
  RATING_PER_STAR,
  REVIEW_COMMENT_RULES,
  saveSharePubliclyChoice,
} from './utils'

interface FormData extends MealReviewFormValues {
  sharePublicly: boolean
}

const MealRatingCard = ({
  meal,
  isOnboarding = false,
  onMealReviewCreated,
  sourceID,
}: {
  meal: OrderHistoryReceiptMeal
  onMealReviewCreated(rating: number): void
  isOnboarding?: boolean
  sourceID?: string
}) => {
  const { pathname } = useLocation()
  const { user } = useUser()
  const reviewSourceID = getReviewSourceID({ pathname, sourceID })

  const imageURL = getMealImageURL(meal)

  const {
    data: ratingChips,
    error: loadRatingChipsError,
    isError: hasLoadRatingChipsError,
  } = useQuery(reviewQueries.ratingChips())

  const {
    error: createMealReviewError,
    isError: hasCreateMealReviewError,
    isPending: isCreatingMealReview,
    mutate: createMealReview,
  } = useCreateMealReview({
    mealID: meal.id,
    onSuccess: (_response, { data }) => {
      saveSharePubliclyChoice({
        sharePublicly: data.sharePublicly ?? true,
        userID: user.id,
      })
      onMealReviewCreated(data.rating)

      track(events.ORDERS_MEAL_REVIEW_COMPLETED, {
        meal_id: meal.id,
        source_id: reviewSourceID,
      })
    },
  })

  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
  } = useForm<FormData>({
    defaultValues: {
      comment: '',
      rating: '',
      selectedChipIDs: [],
      sharePublicly: getLastSharePubliclyChoice(user.id),
    },
  })

  const values = watch()
  const selectedRatingNumber = values.rating ? Number(values.rating) : 0
  const isReviewStarted = values.rating !== ''
  const isImageCompact = isReviewStarted && !isOnboarding

  const chipsForRating =
    ratingChips && selectedRatingNumber
      ? getRatingChipsForRating({
          chips: ratingChips.chips,
          rating: selectedRatingNumber,
        })
      : []

  const handleRatingSubmit = (values: FormData) => {
    track(events.REVIEW_SUBMITTED, {
      meal_id: meal.id,
      source_id: reviewSourceID,
    })

    createMealReview({
      data: getCreateMealReviewData({
        chips: ratingChips?.chips ?? [],
        comment: values.comment,
        mealID: meal.id,
        rating: Number(values.rating),
        selectedChipIDs: values.selectedChipIDs,
        sharePublicly: values.sharePublicly,
        userID: user.id,
      }),
    })
  }

  useEffect(() => {
    reset()
  }, [meal.id, reset])

  useEffect(() => {
    track(events.ORDERS_MEAL_REVIEW_SHOWN, {
      meal_id: meal.id,
      source_id: reviewSourceID,
    })
  }, [meal.id, reviewSourceID])

  return (
    <AnimatePresence>
      <form key={meal.id} onSubmit={handleSubmit(handleRatingSubmit)}>
        <div
          className={clsx('space-y-4 px-4 pt-4 text-center', {
            'pb-6': isReviewStarted,
          })}
        >
          <p className="mx-auto max-w-[331px] text-k/20_110 text-grey-10">
            {meal.title}
          </p>

          <div
            className={clsx(
              'relative mx-auto overflow-hidden rounded-xl',
              isImageCompact
                ? 'h-[117px] w-[156px]'
                : 'h-[286px] w-full max-w-[382px] md:h-[259px] md:max-w-none',
            )}
          >
            {imageURL ? (
              <img
                alt=""
                className="h-full w-full object-cover"
                src={`https://${imageURL}`}
              />
            ) : (
              <div className="h-full w-full bg-grey-3" />
            )}

            {isOnboarding && (
              <div className="absolute left-2 top-2 flex flex-col items-start space-y-[1px]">
                <MealRatingBadge
                  stars={
                    isReviewStarted
                      ? selectedRatingNumber / RATING_PER_STAR
                      : undefined
                  }
                />
                {isReviewStarted && (
                  <div className="-ml-[5px]">
                    <RatingOnboardingCallout />
                  </div>
                )}
              </div>
            )}
          </div>

          {!isReviewStarted && (
            <h2 className="pt-4 text-k/24_120">How Was Your Meal?</h2>
          )}

          <div className="flex justify-center">
            <RatingStars
              idPrefix={`${meal.id}`}
              mealID={meal.id}
              registration={register('rating')}
              selectedRating={selectedRatingNumber}
              size={isReviewStarted ? 'compact' : 'large'}
              sourceID={reviewSourceID}
            />
          </div>

          {isReviewStarted && (
            <>
              {hasLoadRatingChipsError ? (
                <APIErrorDisplay
                  error={loadRatingChipsError}
                  errorCodeMessageMap={LOAD_RATING_CHIPS_ERRORS}
                />
              ) : (
                chipsForRating.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-k/14_120 text-grey-10">
                      Tap any that apply
                    </p>

                    <RatingChipOptions
                      chips={chipsForRating}
                      idPrefix={`${meal.id}`}
                      mealID={meal.id}
                      registration={register('selectedChipIDs')}
                      selectedChipIDs={values.selectedChipIDs}
                    />
                  </div>
                )
              )}

              <div className="pt-2">
                <ReviewCommentField
                  errorMessage={errors.comment?.message}
                  id={`${meal.id}-comment`}
                  onToggleSharePublicly={() => {
                    setValue('sharePublicly', !values.sharePublicly)
                  }}
                  registration={register('comment', REVIEW_COMMENT_RULES)}
                  sharePublicly={values.sharePublicly}
                />
              </div>

              <SharePubliclyToggle
                id={`${meal.id}-share-publicly`}
                registration={register('sharePublicly')}
                sharePublicly={values.sharePublicly}
              />
            </>
          )}
        </div>

        {isReviewStarted ? (
          <div className="sticky bottom-0 space-y-4 bg-white px-4 py-3">
            {hasCreateMealReviewError && (
              <APIErrorDisplay
                error={createMealReviewError}
                errorCodeMessageMap={CREATE_REVIEW_ERRORS}
              />
            )}

            <div className="flex justify-center">
              <ButtonLoading
                isLoading={isCreatingMealReview}
                name={`${meal.id}-submit`}
                size="large"
                type="submit"
              >
                Submit
              </ButtonLoading>
            </div>
          </div>
        ) : (
          <div className="pb-6" />
        )}
      </form>
    </AnimatePresence>
  )
}

export default MealRatingCard
